import { set } from 'lodash';

/**
 * the base colors defined here never change. White will always equal #FFF. If a client needs to
 * add a unique color (like Coca-Cola Red) then you would define that color here first then
 * add a themeColor variable that references the newly added unique baseColor.
 *
 * These baseColor variables cannot be overwritten.
 *
 * @returns {{greenncr: string, boulder: string, 'very-light-gray': string, 'blue-dianne': string, alto: string, mantis: string, verylightgrey: string, red: string, lightgrey: string, 'dove-grey': string, white: string, bluedianne: string, 'dove-gray': string, 'blue-dianne-light': string, tangaroadark: string, greencr: string, gallery: string, lightgray: string, 'very-light-grey': string, blackish: string, seablue: string, bluediannelight: string, black: string, 'tangaroa-dark': string, malibu: string, 'light-gray': string, 'sea-blue': string, 'green-ncr': string, verylightgray: string, light: string, dovegray: string, tangaroa: string, 'light-grey': string, dovegrey: string}}
 */
export const baseColors = () => {
  /* see https://app.zeplin.io/project/5d4fc6678866d6522ffbaf2c/styleguide/colors for details */
  return {
    /* grey tones */
    white: '#FFF',
    'very-light-grey': '#F4F4F4',
    verylightgrey: '#F4F4F4',
    'very-light-gray': '#F4F4F4',
    verylightgray: '#F4F4F4',
    bombaygray: '#B3B8BC',
    light: '#F4F4F4',
    lightsemitransparent: '#F4F4F44C', // 30% transparency
    grayFA: '#fafafa',
    greyFA: '#fafafa',
    gallery: '#EDEDED',
    'light-grey': '#E5E5E5',
    lightgrey: '#E5E5E5',
    'light-gray': '#E5E5E5',
    lightgray: '#E5E5E5',
    darkerlightgray: '#d3d3d3',
    darkgray: '#979797',
    gainsboro: '#DBDBDB',
    alto: '#DDDCDC', // also includes #D7D7D7
    mediumgrey: '#cdd1d3',
    'dove-grey': '#666',
    'dove-gray': '#666',
    dovegrey: '#666',
    dovegray: '#666',
    boulder: '#757575',
    almostwhite: '#00000042', // 26% transparency
    blackish: '#00000099', // 60% transparency
    almostblack: '#000000DE', // 87% transparency
    blacktransparent: 'rgba(0, 0, 0, 0.0)',
    black: '#000',
    almosttransparentblack: 'rgba(0, 0, 0, 0.05)',
    semitransparentblack: 'rgba(0, 0, 0, 0.14)',
    blackpearl: '#03061E',
    /* colorful tones */
    darkgreen: '#4AA73D',
    'sea-blue': '#72D1EE',
    seablue: '#72D1EE',
    aliceblue: '#F1FAFD',
    watermarkblue: '#C7EDF8',
    malibu: '#81DAF580', // 50% transparency
    'blue-dianne': '#264558',
    bluedianne: '#264558',
    'blue-dianne-light': '#255A6A',
    bluediannelight: '#255A6A',
    bluewhale: '#1f3341',
    tangaroa: '#1A2D38',
    'tangaroa-dark': '#08273C',
    tangaroadark: '#08273C',
    mantis: '#76C76C',
    deyork: '#98CF91',
    'green-ncr': '#54AF48',
    greenncr: '#54AF48',
    greencr: '#54AF48',
    ncrgreen: '#54AF48',
    red: '#D0021B',
    palesky: '#5D6B72',
    pattensblue: '#D4DADE',
    ntep: '#A0A8AC',
    /* custom client colors go here */
    // cocacolared: '#FE001A', // for example
    funGreen: '#A5CE39',
    aldiBlue: '#14215A',
    scoGreen: '#A6CE39',
    scoBlue: '#18565E',
    bigstone: '#30424B',
    ncrBrandGreen: '#51AF46',
    pinBtnGrey: '#eeeeee',
    gainsboroGray: '#E0E0E0',
    returnModeWatermark: '#A0A8AC',
    disabledTextGrey: '#A6A6A6',
    subtleDisabledTextGrey: '#A8B5BC',
    gullGray: '#93A2AB',
    darkBlue: '#004E98',
    rollingStone: '#747F86',
    buttonicongrey: '#677D8A',
    passiveGreen: '#DDEFDA',
  };
};

const deuteranopiaColors = () => {
  return {
    aliceblue: '#fff6fb',
    alto: '#f0d4dd',
    bluedianne: '#3d435a',
    bluediannedlight: '#4f536d',
    boulder: '#7f7176',
    deyork: '#e2b897',
    dovegrey: '#706367',
    light: '#fff0f5',
    lightgrey: '#fadde7',
    lightsemitransparent: '#fff0f54c',
    malibu: '#c8c8fb',
    mantis: '#d9ac74',
    mediumgrey: '#e2c9d4',
    ncrgreen: '#be9353',
    ntep: '#b2a0ad',
    palesky: '#6e6673',
    pattensblue: '#ead1e0',
    red: '#836103',
    seablue: '#bcbff4',
    tangaroa: '#2c2e3c',
    tangaroadark: '#1e293f',
    verylightgrey: '#fff0f5',
  };
};

const protanopiaColors = () => {
  return {
    aliceblue: '#fcf7fb',
    alto: '#dfdadb',
    bluedianne: '#3e4356',
    bluediannedlight: '#505365',
    boulder: '#767474',
    deyork: '#cfbf8a',
    dovegrey: '#686566',
    light: '#f8f2f3',
    lightgrey: '#e8e3e4',
    lightsemitransparent: '#f8f2f34c',
    malibu: '#c6cbec',
    mantis: '#c5b365',
    mediumgrey: '#d2ced1',
    ncrgreen: '#ac9b44',
    ntep: '#a7a4a9',
    palesky: '#686870',
    pattensblue: '#dbd7dc',
    red: '#726732',
    seablue: '#bcc1e4',
    tangaroa: '#2c2e39',
    tangaroadark: '#23293c',
    verylightgrey: '#f8f2f3',
  };
};

const tritanopiaColors = () => {
  return {
    aliceblue: '#6dd2e2',
    alto: '#dfd9ea',
    bluedianne: '#26494e',
    bluediannedlight: '#265c63',
    boulder: '#76737c',
    deyork: '#a5c5d4',
    dovegrey: '#67656d',
    light: '#f6f2ff',
    lightgrey: '#e7e2f3',
    lightsemitransparent: '#f6f2ff4c',
    malibu: '#7ddbec',
    mantis: '#8abbc9',
    mediumgrey: '#cecede',
    ncrgreen: '#6da3af',
    ntep: '#a0a5b2',
    palesky: '#5e6a73',
    pattensblue: '#d5d7e8',
    red: '#ce1100',
    seablue: '#6dd2e2',
    tangaroa: '#1e3236',
    tangaroadark: '#002e31',
    verylightgrey: '#f6f2ff',
  };
};
/**
 * when a color has the potential to be themed by a client (e.g., Kroger vs. Publix) then you
 * should define a variable (e.g, alert), assign it to a baseColor (e.g., red), then use the
 * variable in your code (themeColor.alert versus baseColor.red)
 *
 * These themeColor variables can be overwritten.
 *
 * @param overrides
 * @returns {{modalBackground: string, secondaryContrast: string, modalTitle: string, secondaryContrastActive: string, alert: string, itemInfoText: string}}
 */
export const themeColors = overrides => {
  const color = defaultThemeColors();
  if (overrides) {
    for (const key of Object.keys(overrides)) {
      color[key] = overrides[key];
    }
  }

  return color;
};

export const defaultThemeColors = () => {
  return createColorDefinitions(baseColors());
};

export const deuteranopiaThemeColors = () => {
  return createColorDefinitions(deuteranopiaColors());
};

export const protanopiaThemeColors = () => {
  return createColorDefinitions(protanopiaColors());
};

export const tritanopiaThemeColors = () => {
  return createColorDefinitions(tritanopiaColors());
};

const createColorDefinitions = colorPalette => {
  const baseColor = baseColors();
  return {
    /* Base Colors */
    almostblack: baseColor.almostblack,
    almosttransparentblack: baseColor.almosttransparentblack,
    almostwhite: baseColor.almostwhite,
    black: baseColor.black,
    blackish: baseColor.blackish,
    blackpearl: baseColor.blackpearl,
    blacktransparent: baseColor.blacktransparent,
    gainsboro: baseColor.gainsboro,
    gallery: baseColor.gallery,
    lightgray: baseColor.lightgray,
    lightgrey: baseColor.lightgrey,
    darkerlightgray: baseColor.darkerlightgray,
    semitransparentblack: baseColor.semitransparentblack,
    verylightgrey: baseColor.verylightgrey,
    white: baseColor.white,
    returnModeWatermark: baseColor.returnModeWatermark,
    lockTextColor: baseColor.gullGray,
    disabledTextGrey: baseColor.bombaygray,
    darkGreen: baseColor.darkgreen,

    /*Overridable Colors */
    primaryMain: colorPalette.bluedianne,
    // primaryLight is not defined
    primaryDark: colorPalette.bluediannelight,
    secondaryMain: colorPalette.ncrgreen,
    secondaryLight: colorPalette.lightgrey,
    // secondaryDark is not defined
    infoMain: colorPalette.boulder,
    // infoLight is not defined
    infoDark: colorPalette.dovegrey,
    /* themed colors - alphabetical */
    alert: colorPalette.red,
    bigstone: colorPalette.bigstone,
    blueBackground: colorPalette.seablue,
    borderGray: colorPalette.alto,
    buttonIconGrey: colorPalette.buttonicongrey,
    defaultTextColor: colorPalette.tangaroa,
    defaultDarkTextColor: colorPalette.tangaroadark,
    grayDivider: colorPalette.mediumgrey,
    itemInfoText: colorPalette.tangaroadark,
    lightBackground: colorPalette.light,
    lightBackgroundSemiTransparent: colorPalette.lightsemitransparent,
    modalBackground: colorPalette.verylightgrey,
    modalBackgroundColor: colorPalette.tangaroa,
    modalTitle: colorPalette.tangaroa,
    ntepText: colorPalette.ntep,
    palesky: baseColor.palesky,
    passiveGreen: baseColor.passiveGreen,
    progressBar: colorPalette.pattensblue,
    rollingStone: colorPalette.rollingStone,
    secondaryContrast: colorPalette.mantis,
    secondaryContrastActive: colorPalette.mantis,
    secondaryContrastDisabled: colorPalette.deyork,
    selectedDropdown: `${colorPalette.mantis}4D`,
    selectedPrimary: colorPalette.malibu,
    subheaderLight: colorPalette.palesky,
    subtleDisabledTextGrey: colorPalette.subtleDisabledTextGrey,
    trainingColor: colorPalette.seablue,
    trainingBackground: colorPalette.aliceblue,
    warning: colorPalette.bluedianne,
    watermark: colorPalette.watermarkblue,
  };

  return color;
};

/**
 * this theme is specific to MUI components, for example when you type color="primary" in a button
 * component; it will grab the theme.palette.primary.main color (which is defaulted to bluedianne).
 *
 * if you define a light or dark color; MUI components will use the defined color; otherwise MUI
 * will use darken(10%) or lighten(10%) based off the main color.
 *
 * NOTE: these colors should never be referenced directly in CSS/JSX; for example, you want to use
 * themeColor.INSERT_THEMECOLOR_HERE instead of theme.palette.primary.main in your code.
 *
 * @param overrides
 * @returns {{palette: {secondary: {light: string, main: string}, primary: {dark: string, main: string}, info: {dark: string, main: string}}}}
 */
export const createColorBlindNcrRetailTheme = colorPalette => {
  const themeColor = colorPalette;
  const theme = createBaseNcrRetailTheme(themeColor);

  return theme;
};

const createNcrRetailTheme = overrides => {
  const themeColor = themeColors();
  /* zeplin theme names */
  const theme = createBaseNcrRetailTheme(themeColor);
  /* overrides should follow the lodash set notation where key is a path of the object separated by periods */
  if (overrides) {
    for (const key of Object.keys(overrides)) {
      set(theme, key, overrides[key]);
    }
  }
  return theme;
};

const createBaseNcrRetailTheme = colorPalette => {
  const themeColor = colorPalette;
  /* zeplin theme names */
  const theme = {
    palette: {
      primary: {
        main: themeColor.primaryMain,
        /* used for focused buttons; see Emerald Styleguide on Zeplin */
        dark: themeColor.primaryDark,
      },
      secondary: {
        main: themeColor.secondaryMain,
        // light: themeColor.secondaryLight,
      },
      info: {
        main: themeColor.infoMain,
        dark: themeColor.infoDark,
      },
      error: {
        main: themeColor.alert,
      },
    },
    overrides: {
      MuiSelect: {
        select: {
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
  };

  return theme;
};

export default createNcrRetailTheme;
